import { useDeferredValue, useEffect, useMemo, useState } from 'react'
import { api } from './api'
import { ActivityRail } from './components/ActivityRail'
import { AdminLogin } from './components/AdminLogin'
import { EntitlementsTable } from './components/EntitlementsTable'
import { Filters } from './components/Filters'
import { Inspector } from './components/Inspector'
import {
  ActivationsPanel,
  AuditLogsPanel,
  CustomersPanel,
  JobsPanel,
} from './components/OperationsTables'
import { PilotCohort } from './components/PilotCohort'
import { PublishDialog } from './components/PublishDialog'
import { Releases } from './components/Releases'
import { Sidebar } from './components/Sidebar'
import { Summary } from './components/Summary'
import { Topbar } from './components/Topbar'
import { VersionAdoption } from './components/VersionAdoption'
import type {
  ActivationsPage,
  AdminSection,
  AuditLogsPage,
  CustomersPage,
  DashboardData,
  EntitlementDetail,
  EntitlementsPage,
  JobsPage,
  PilotMembersPage,
} from './types'

const PAGE_SIZE = 25
const sections: AdminSection[] = [
  'overview',
  'customers',
  'entitlements',
  'activations',
  'releases',
  'commerce',
  'audit-log',
]
const sectionTitles: Record<AdminSection, string> = {
  overview: 'Operations overview',
  customers: 'Customer operations',
  entitlements: 'Entitlement operations',
  activations: 'Activation operations',
  releases: 'Release operations',
  commerce: 'Commerce operations',
  'audit-log': 'Audit history',
}

function sectionFromHash(): AdminSection {
  const candidate = window.location.hash.replace(/^#\/?/, '') as AdminSection
  return sections.includes(candidate) ? candidate : 'overview'
}

function errorMessage(error: unknown, fallback: string) {
  return error instanceof Error ? error.message : fallback
}

export function App() {
  const [section, setSection] = useState<AdminSection>(sectionFromHash)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [data, setData] = useState<DashboardData | null>(null)
  const [entitlements, setEntitlements] = useState<EntitlementsPage | null>(null)
  const [detail, setDetail] = useState<EntitlementDetail | null>(null)
  const [customers, setCustomers] = useState<CustomersPage | null>(null)
  const [activations, setActivations] = useState<ActivationsPage | null>(null)
  const [jobs, setJobs] = useState<JobsPage | null>(null)
  const [auditLogs, setAuditLogs] = useState<AuditLogsPage | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [listBusy, setListBusy] = useState(false)
  const [globalQuery, setGlobalQuery] = useState('')
  const [tableQuery, setTableQuery] = useState('')
  const [product, setProduct] = useState('All')
  const [channel, setChannel] = useState('All')
  const [status, setStatus] = useState('All')
  const [offset, setOffset] = useState(0)
  const [customerQuery, setCustomerQuery] = useState('')
  const [customerOffset, setCustomerOffset] = useState(0)
  const [activationQuery, setActivationQuery] = useState('')
  const [activationProduct, setActivationProduct] = useState('')
  const [activationStatus, setActivationStatus] = useState('')
  const [activationOffset, setActivationOffset] = useState(0)
  const [jobStatus, setJobStatus] = useState('')
  const [jobType, setJobType] = useState('')
  const [jobOffset, setJobOffset] = useState(0)
  const [auditQuery, setAuditQuery] = useState('')
  const [auditOutcome, setAuditOutcome] = useState('')
  const [auditOffset, setAuditOffset] = useState(0)
  const [revision, setRevision] = useState(0)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [showPublish, setShowPublish] = useState(false)
  const [pilotMembers, setPilotMembers] = useState<PilotMembersPage | null>(null)
  const [pilotBusy, setPilotBusy] = useState(false)
  const [pilotQuery, setPilotQuery] = useState('')
  const [adminRole, setAdminRole] = useState('')
  const [archivingReleaseId, setArchivingReleaseId] = useState<string | null>(null)
  const [retryingJobId, setRetryingJobId] = useState<string | null>(null)
  const [authRequired, setAuthRequired] = useState(false)
  const [authBusy, setAuthBusy] = useState(true)
  const [sessionReady, setSessionReady] = useState(false)

  const deferredEntitlementQuery = useDeferredValue(`${globalQuery} ${tableQuery}`.trim())
  const deferredCustomerQuery = useDeferredValue(`${globalQuery} ${customerQuery}`.trim())
  const deferredActivationQuery = useDeferredValue(`${globalQuery} ${activationQuery}`.trim())
  const deferredJobType = useDeferredValue(jobType.trim().toLowerCase())
  const deferredAuditQuery = useDeferredValue(`${globalQuery} ${auditQuery}`.trim())

  const reloadDashboard = async () => {
    try {
      setData(await api.dashboard())
      setError('')
    } catch (requestError) {
      setError(errorMessage(requestError, 'Unable to load dashboard'))
    }
  }

  const reloadPilot = async (query = '') => {
    setPilotBusy(true)
    try {
      setPilotMembers(await api.pilotMembers(query))
      setError('')
    } catch (requestError) {
      setError(errorMessage(requestError, 'Unable to load pilot cohort'))
    } finally {
      setPilotBusy(false)
    }
  }

  useEffect(() => {
    const handleHash = () => setSection(sectionFromHash())
    window.addEventListener('hashchange', handleHash)
    return () => window.removeEventListener('hashchange', handleHash)
  }, [])

  useEffect(() => {
    const demoToken = import.meta.env.VITE_ADMIN_BOOTSTRAP_TOKEN ?? 'curic-demo-admin'
    void api
      .authenticate(demoToken)
      .then((session) => {
        setAdminRole(session.role)
        setSessionReady(true)
        return Promise.all([
          reloadDashboard(),
          ['support', 'release_manager', 'admin'].includes(session.role)
            ? reloadPilot()
            : Promise.resolve(),
        ])
      })
      .catch((authError: unknown) => {
        setError(errorMessage(authError, 'Admin sign-in failed'))
        setAuthRequired(true)
      })
      .finally(() => setAuthBusy(false))
  }, [])

  useEffect(() => {
    if (!sessionReady || section !== 'entitlements') return
    let cancelled = false
    setListBusy(true)
    void api
      .entitlements({
        query: deferredEntitlementQuery,
        product: product === 'All' ? '' : product,
        channel: channel === 'All' ? '' : channel.toLowerCase(),
        status: status === 'All' ? '' : status.toLowerCase(),
        limit: PAGE_SIZE,
        offset,
      })
      .then((page) => {
        if (cancelled) return
        setEntitlements(page)
        setSelectedId((current) => {
          if (current && page.items.some((row) => row.id === current)) return current
          if (!window.matchMedia('(min-width: 941px)').matches) return null
          return page.items[0]?.id ?? null
        })
        setError('')
      })
      .catch((requestError: unknown) => {
        if (!cancelled) setError(errorMessage(requestError, 'Unable to load entitlements'))
      })
      .finally(() => {
        if (!cancelled) setListBusy(false)
      })
    return () => {
      cancelled = true
    }
  }, [channel, deferredEntitlementQuery, offset, product, revision, section, sessionReady, status])

  useEffect(() => {
    if (!sessionReady || section !== 'entitlements' || !selectedId) {
      setDetail(null)
      return
    }
    let cancelled = false
    void api.entitlement(selectedId).then((next) => {
      if (!cancelled) setDetail(next)
    }).catch((requestError: unknown) => {
      if (!cancelled) setError(errorMessage(requestError, 'Unable to load entitlement details'))
    })
    return () => {
      cancelled = true
    }
  }, [revision, section, selectedId, sessionReady])

  useEffect(() => {
    if (!sessionReady || section !== 'customers') return
    let cancelled = false
    setListBusy(true)
    void api.customers({ query: deferredCustomerQuery, limit: PAGE_SIZE, offset: customerOffset })
      .then((page) => { if (!cancelled) { setCustomers(page); setError('') } })
      .catch((requestError: unknown) => { if (!cancelled) setError(errorMessage(requestError, 'Unable to load customers')) })
      .finally(() => { if (!cancelled) setListBusy(false) })
    return () => { cancelled = true }
  }, [customerOffset, deferredCustomerQuery, revision, section, sessionReady])

  useEffect(() => {
    if (!sessionReady || section !== 'activations') return
    let cancelled = false
    setListBusy(true)
    void api.activations({
      query: deferredActivationQuery,
      product: activationProduct,
      status: activationStatus,
      limit: PAGE_SIZE,
      offset: activationOffset,
    }).then((page) => { if (!cancelled) { setActivations(page); setError('') } })
      .catch((requestError: unknown) => { if (!cancelled) setError(errorMessage(requestError, 'Unable to load activations')) })
      .finally(() => { if (!cancelled) setListBusy(false) })
    return () => { cancelled = true }
  }, [activationOffset, activationProduct, activationStatus, deferredActivationQuery, revision, section, sessionReady])

  useEffect(() => {
    if (!sessionReady || section !== 'commerce') return
    let cancelled = false
    setListBusy(true)
    void api.jobs({ status: jobStatus, jobType: deferredJobType, limit: 50, offset: jobOffset })
      .then((page) => { if (!cancelled) { setJobs(page); setError('') } })
      .catch((requestError: unknown) => { if (!cancelled) setError(errorMessage(requestError, 'Unable to load commerce jobs')) })
      .finally(() => { if (!cancelled) setListBusy(false) })
    return () => { cancelled = true }
  }, [deferredJobType, jobOffset, jobStatus, revision, section, sessionReady])

  useEffect(() => {
    if (!sessionReady || section !== 'audit-log') return
    let cancelled = false
    setListBusy(true)
    void api.auditLogs({ query: deferredAuditQuery, outcome: auditOutcome, limit: 50, offset: auditOffset })
      .then((page) => { if (!cancelled) { setAuditLogs(page); setError('') } })
      .catch((requestError: unknown) => { if (!cancelled) setError(errorMessage(requestError, 'Unable to load audit history')) })
      .finally(() => { if (!cancelled) setListBusy(false) })
    return () => { cancelled = true }
  }, [auditOffset, auditOutcome, deferredAuditQuery, revision, section, sessionReady])

  const authenticate = async (bootstrapToken: string) => {
    setAuthBusy(true)
    try {
      const session = await api.authenticate(bootstrapToken)
      setAdminRole(session.role)
      setAuthRequired(false)
      setSessionReady(true)
      setError('')
      await Promise.all([
        reloadDashboard(),
        ['support', 'release_manager', 'admin'].includes(session.role) ? reloadPilot() : Promise.resolve(),
      ])
    } catch (authError) {
      setError(errorMessage(authError, 'Admin sign-in failed'))
    } finally {
      setAuthBusy(false)
    }
  }

  const navigate = (next: AdminSection, query = '') => {
    setGlobalQuery(query)
    window.location.hash = next
    setSection(next)
  }

  const products = useMemo(() => Array.from(new Set([
    ...(data?.entitlements.map((row) => row.product) ?? []),
    ...(entitlements?.items.map((row) => row.product) ?? []),
    ...(activations?.items.map((row) => row.product) ?? []),
  ])).sort(), [activations, data, entitlements])
  const selected = detail?.entitlement.id === selectedId ? detail.entitlement : null
  const selectedActivations = detail?.entitlement.id === selectedId ? detail.activations : []

  const patchSelected = async (patch: {
    seat_count?: number
    seat_limit_enforced?: boolean
    updates_until?: string
    channel?: string
    status?: string
  }) => {
    if (!selected) return
    setBusy(true)
    try {
      await api.patchEntitlement(selected.id, patch)
      const [nextDashboard, nextDetail] = await Promise.all([api.dashboard(), api.entitlement(selected.id)])
      setData(nextDashboard)
      setDetail(nextDetail)
      setRevision((value) => value + 1)
      setError('')
    } catch (requestError) {
      setError(errorMessage(requestError, 'Update failed'))
    } finally {
      setBusy(false)
    }
  }

  const deactivate = async (activationId: string) => {
    setBusy(true)
    try {
      await api.deactivate(activationId)
      setData(await api.dashboard())
      if (selectedId) setDetail(await api.entitlement(selectedId))
      setRevision((value) => value + 1)
      setError('')
    } catch (requestError) {
      setError(errorMessage(requestError, 'Deactivation failed'))
    } finally {
      setBusy(false)
    }
  }

  const revokeActivation = async (activationId: string, reason: string) => {
    setBusy(true)
    try {
      await api.revokeActivation(activationId, reason)
      setData(await api.dashboard())
      if (selectedId) setDetail(await api.entitlement(selectedId))
      setRevision((value) => value + 1)
      setError('')
    } catch (requestError) {
      setError(errorMessage(requestError, 'Revocation failed'))
    } finally {
      setBusy(false)
    }
  }

  const archiveRelease = async (releaseId: string) => {
    setArchivingReleaseId(releaseId)
    try {
      await api.archiveRelease(releaseId)
      await reloadDashboard()
    } catch (requestError) {
      setError(errorMessage(requestError, 'Release archive failed'))
    } finally {
      setArchivingReleaseId(null)
    }
  }

  const retryJob = async (jobId: string) => {
    setRetryingJobId(jobId)
    try {
      await api.retryJob(jobId)
      setRevision((value) => value + 1)
      setError('')
    } catch (requestError) {
      setError(errorMessage(requestError, 'Job retry failed'))
    } finally {
      setRetryingJobId(null)
    }
  }

  const enrollPilotMember = async (email: string, note: string) => {
    setPilotBusy(true)
    try {
      await api.enrollPilotMember(email, note)
      setPilotMembers(await api.pilotMembers(pilotQuery))
      setError('')
      return true
    } catch (requestError) {
      setError(errorMessage(requestError, 'Pilot enrollment failed'))
      return false
    } finally {
      setPilotBusy(false)
    }
  }

  const removePilotMember = async (userId: string) => {
    setPilotBusy(true)
    try {
      await api.removePilotMember(userId)
      setPilotMembers(await api.pilotMembers(pilotQuery))
      setError('')
      return true
    } catch (requestError) {
      setError(errorMessage(requestError, 'Pilot removal failed'))
      return false
    } finally {
      setPilotBusy(false)
    }
  }

  if (authRequired) return <AdminLogin error={error} busy={authBusy} onSubmit={authenticate} />
  if (!data && (!error || authBusy)) return <main className="loading-screen"><span className="brand-mark">C</span><p>Loading license operations…</p></main>
  if (!data) return (
    <main className="loading-screen error-screen"><span className="brand-mark">C</span><h1>Curic Cloud is unavailable</h1><p>{error}</p>
      <button className="primary-button" type="button" onClick={() => void reloadDashboard()}>Retry</button>
    </main>
  )

  return (
    <div className={`app-shell ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      <Sidebar active={section} collapsed={sidebarCollapsed} onNavigate={navigate} onCollapse={() => setSidebarCollapsed((value) => !value)} />
      <div className="workspace">
        <Topbar query={globalQuery} onQueryChange={(value) => { setGlobalQuery(value); setOffset(0); setCustomerOffset(0); setActivationOffset(0); setAuditOffset(0) }} role={adminRole} />
        <main className="main-content">
          <div className="content-heading"><div><h1>{sectionTitles[section]}</h1><p className="page-subtitle">Signed in with {adminRole.replace('_', ' ')} access</p></div>{error && <span className="error-toast">{error}</span>}</div>

          {section === 'overview' && <>
            <Summary activeEntitlements={data.active_entitlements} activeDevices={data.active_devices} adoption={data.update_adoption} webhookHealthy={data.webhook_healthy} />
            {pilotMembers && <PilotCohort busy={pilotBusy} canManage={adminRole === 'admin'} onEnroll={enrollPilotMember} onRefresh={() => reloadPilot(pilotQuery)} onRemove={removePilotMember} onSearch={async (query) => { setPilotQuery(query); await reloadPilot(query) }} page={pilotMembers} />}
            <VersionAdoption rows={data.version_adoption} />
          </>}

          {section === 'customers' && <CustomersPanel page={customers} query={customerQuery} busy={listBusy} onQueryChange={(value) => { setCustomerQuery(value); setCustomerOffset(0) }} onPageChange={setCustomerOffset} onOpenEntitlements={(email) => navigate('entitlements', email)} />}

          {section === 'entitlements' && <div className="operations-grid">
            <section className="entitlements-panel"><header className="section-toolbar"><h2>Entitlements</h2><Filters product={product} channel={channel} status={status} search={tableQuery} products={products} onProduct={(value) => { setProduct(value); setOffset(0) }} onChannel={(value) => { setChannel(value); setOffset(0) }} onStatus={(value) => { setStatus(value); setOffset(0) }} onSearch={(value) => { setTableQuery(value); setOffset(0) }} /></header>
              <EntitlementsTable rows={entitlements?.items ?? []} total={entitlements?.total ?? 0} limit={entitlements?.limit ?? PAGE_SIZE} offset={entitlements?.offset ?? offset} busy={listBusy} selectedId={selectedId} onSelect={setSelectedId} onPageChange={setOffset} />
            </section>
            {selected && <Inspector entitlement={selected} activations={selectedActivations} busy={busy} canEdit={adminRole === 'admin'} canDeactivate={adminRole !== 'viewer'} canRevoke={adminRole === 'admin'} onClose={() => setSelectedId(null)} onDeactivate={deactivate} onRevoke={revokeActivation} onPatch={patchSelected} onRefresh={async () => { if (selectedId) setDetail(await api.entitlement(selectedId)) }} />}
          </div>}

          {section === 'activations' && <ActivationsPanel page={activations} query={activationQuery} status={activationStatus} products={products} product={activationProduct} busy={listBusy || busy} canDeactivate={adminRole !== 'viewer'} canRevoke={adminRole === 'admin'} onQueryChange={(value) => { setActivationQuery(value); setActivationOffset(0) }} onStatusChange={(value) => { setActivationStatus(value); setActivationOffset(0) }} onProductChange={(value) => { setActivationProduct(value); setActivationOffset(0) }} onPageChange={setActivationOffset} onDeactivate={deactivate} onRevoke={revokeActivation} />}

          {section === 'releases' && <Releases releases={data.releases} archivingId={archivingReleaseId} canManage={['release_manager', 'admin'].includes(adminRole)} onPublish={() => setShowPublish(true)} onArchive={archiveRelease} />}

          {section === 'commerce' && <JobsPanel page={jobs} status={jobStatus} jobType={jobType} busy={listBusy} canRetry={adminRole === 'admin'} retryingId={retryingJobId} onStatusChange={(value) => { setJobStatus(value); setJobOffset(0) }} onJobTypeChange={(value) => { setJobType(value); setJobOffset(0) }} onPageChange={setJobOffset} onRetry={retryJob} />}

          {section === 'audit-log' && <AuditLogsPanel page={auditLogs} query={auditQuery} outcome={auditOutcome} busy={listBusy} onQueryChange={(value) => { setAuditQuery(value); setAuditOffset(0) }} onOutcomeChange={(value) => { setAuditOutcome(value); setAuditOffset(0) }} onPageChange={setAuditOffset} />}
        </main>
      </div>
      <ActivityRail events={data.activity} onViewAll={() => navigate('audit-log')} />
      {showPublish && <PublishDialog onClose={() => setShowPublish(false)} onPublished={reloadDashboard} />}
    </div>
  )
}
